import { useCallback, useEffect, useRef } from "react";
import { TableContextActions, TableContextValue } from "./types";
import { buildFetchParams } from "./utils";
import { useUserContext } from "../use-auth-context";
import { useScreenAlert } from "../use-screen-alert";


/**
 * Generic data fetching hook for table contexts.
 * Handles the initial data load and refetching when state changes.
 */
export function useTableDataFetcher<TData, TFilter>({
  state,
  loading,
  otherLoadingStates,
  fetchData,
  getFetchParams,
  setItems,
  checkUserAuthentication,
  setError,
}: {
  state: TableContextValue<TData, TFilter>;
  loading: boolean;
  otherLoadingStates: boolean[];
  fetchData: (params: any) => Promise<{ items: TData; total: number } | null>;
  getFetchParams: (filters: TFilter[], perPage: number, page: number) => any;
  setItems: (data: { items: TData; total: number }) => void;
  checkUserAuthentication: () => Promise<any>;
  setError: (error: string) => void;
}) {
  const fetching = useRef(false);
  const isAnyOtherLoading = otherLoadingStates.some((loadingState) => loadingState);

  useEffect(() => {
    const params = getFetchParams(state.filters, state.perPage, state.page);

    const getInitialData = async () => {
      await checkUserAuthentication();
      const result = await fetchData({
        filters: params,
        searchValue: state.searchValue,
      });
      fetching.current = false;
      if (result) setItems(result);
    };

    const isAnyLoading = loading || isAnyOtherLoading;

    if (state.loading && !fetching.current && !isAnyLoading) {
      fetching.current = true;
      getInitialData().catch(() => {
        setItems({
          items: [] as TData,
          total: 0,
        });
        setError("Table action failed. Please try again later.");
      });
    }
  }, [
    loading,
    isAnyOtherLoading,
    state.tableData,
    state.filters,
    state.searchValue,
    state.loading,
    state.perPage,
    state.page,
    setItems,
    fetchData,
    getFetchParams,
    checkUserAuthentication,
    setError,
  ]);
}

/**
 * Creates common table action callbacks that simply dispatch to the reducer.
 * These are the same across all table contexts.
 */
export function useCommonTableActions<TData, TFilter>(
    dispatch: React.Dispatch<{ type: TableContextActions; payload: any }>,
    getItems: (params: any) => Promise<{ items: TData; total: number } | null>,
    deleteItem: (itemId: number | string) => Promise<any>,
    updateItem: (itemId: number | string, update: any) => Promise<any>,
    state: TableContextValue<TData, TFilter>,
) {
  const { checkUserAuthentication } = useUserContext();
  const { setError } = useScreenAlert();
  
  const setItems = useCallback(
    ({ items, total }: { items: TData; total: number }) => {
      dispatch({
        type: TableContextActions.SET_ITEMS,
        payload: { items, total },
      });
    },
    [dispatch]
  );

    const refreshItems = useCallback(async () => {
      await checkUserAuthentication();
      const params = buildFetchParams<TFilter, TData>(
        state.filters,
        state.perPage,
        state.page
      );
      const items = await getItems({
        filters: params,
        searchValue: state.searchValue,
      });
      if (items) {
        setItems(items);
      } else {
        setError("Failed to refresh items.");
      }
    }, [
      getItems,
      setItems,
      state.filters,
      state.perPage,
      state.page,
      state.searchValue,
      checkUserAuthentication,
      setError,
    ]);

    const deleteItems = useCallback(async () => {
    await checkUserAuthentication();
    dispatch({
        type: TableContextActions.DELETE_SELECTED_ITEMS,
        payload: undefined,
    })
       await Promise.all(
         state.selectedItems.map((itemId) => deleteItem(itemId))
       );
    
       setTimeout(async () => {
         const params = buildFetchParams<TFilter, TData>(
           state.filters,
           state.perPage,
           state.page
         );
         const ticketUpdate = await getItems({
           filters: params,
         });
         if (ticketUpdate) setItems(ticketUpdate);
         else setError("Failed to delete items.");
       }, 1000); // Delay to allow the state to update
    }, [
    deleteItem,
    setItems,
    getItems,
    state.filters,
    state.selectedItems,
    state.perPage,
    state.page,
    checkUserAuthentication,
    setError,
    dispatch,

    ]);

      const editItems = useCallback(
        async (update: any) => {
          await checkUserAuthentication();
          dispatch({
            type: TableContextActions.EDIT_SELECTED_ITEMS,
            payload: update,
          });
    
          await Promise.all(
            state.selectedItems.map((assetId) =>
              updateItem(assetId, update.formattedUpdate)
            )
          );
          const params = buildFetchParams<TFilter, TData>(
            state.filters,
            state.perPage,
            state.page
          );
          const itemUpdate = await getItems({
            filters: params,
            searchValue: state.searchValue,
          });
          if (itemUpdate) setItems(itemUpdate);
          else setError("Failed to update items.");
        },
        [
          state.filters,
          state.searchValue,
          setItems,
          getItems,
          state.selectedItems,
          updateItem,
          state.page,
          state.perPage,
          checkUserAuthentication,
          setError,
          dispatch,
        ]
      );

  const addFilter = useCallback(
    (filter: TFilter) => {
      dispatch({
        type: TableContextActions.ADD_FILTER,
        payload: filter,
      });
    },
    [dispatch]
  );

  const removeFilter = useCallback(
    (filterToRemove: TFilter | TFilter[]) => {
      dispatch({
        type: TableContextActions.REMOVE_FILTER,
        payload: filterToRemove,
      });
    },
    [dispatch]
  );

  const removeAllFilters = useCallback(() => {
    dispatch({
      type: TableContextActions.REMOVE_ALL_FILTERS,
      payload: undefined,
    });
  }, [dispatch]);

  const setSearchValue = useCallback(
    (searchValue: string) => {
      dispatch({
        type: TableContextActions.SET_SEARCH_VALUE,
        payload: searchValue,
      });
    },
    [dispatch]
  );

  const clearSearchValue = useCallback(() => {
    dispatch({
      type: TableContextActions.SET_SEARCH_VALUE,
      payload: "",
    });
  }, [dispatch]);

  const selectItem = useCallback(
    (itemId: string | string[]) => {
      dispatch({
        type: TableContextActions.SELECT_ITEM,
        payload: itemId,
      });
    },
    [dispatch]
  );

  const setPage = useCallback(
    (page: number) => {
      dispatch({
        type: TableContextActions.SET_PAGE,
        payload: page,
      });
    },
    [dispatch]
  );

  const setPerPage = useCallback(
    (perPage: number) => {
      dispatch({
        type: TableContextActions.SET_PER_PAGE,
        payload: perPage,
      });
    },
    [dispatch]
  );

  const addHiddenColumn = useCallback(
    (column: string) => {
      dispatch({
        type: TableContextActions.SET_HIDDEN_COLUMNS,
        payload: [...(state.hiddenColumns || []), column],
      });
    },
    [dispatch, state.hiddenColumns]
  );

  const removeHiddenColumn = useCallback(
    (column: string) => {
      dispatch({
        type: TableContextActions.SET_HIDDEN_COLUMNS,
        payload: (state.hiddenColumns || []).filter((col) => col !== column),
      });
    },
    [dispatch, state.hiddenColumns]
  );

  return {
    setItems,
    refreshItems,
    deleteItems,
    editItems,
    addFilter,
    removeFilter,
    removeAllFilters,
    setSearchValue,
    clearSearchValue,
    selectItem,
    setPage,
    setPerPage,
    addHiddenColumn,
    removeHiddenColumn,
  };
}
